import React, { useMemo, useEffect, useState } from "react"
import {
  Container,
  CardBody,
  Card,
  Modal,
  ModalHeader,
  ModalBody,
  Table,
} from "reactstrap"
import practice_logo from "../../assets/images/users/profile.png"
import Breadcrumb from "components/Common/Breadcrumb"
import { useNavigate } from "react-router-dom"
import moment from "moment"
import { axiosbaseurl } from "configure"
import Loader from "components/Common/Loader"
import { usePermissionData } from "AppContext"
import PermittedComponent from "components/Common/PermittedComponent"
import CommonTable from "components/Common/CommonTable"
import * as XLSX from "xlsx"

const EmployeeWeeklyReport = () => {
  const [table_data, settable_data] = useState([])
  const [loading, setLoading] = useState(false)
  const [logsModal, setLogsModal] = useState(false)
  const [selectedLogs, setSelectedLogs] = useState([])

  const toggleLogsModal = () => setLogsModal(!logsModal)

  useEffect(() => {
    const getLogs = async () => {
      try {
        setLoading(true)
        const response = await axiosbaseurl.get(
          `/reports/employee/weekly/report`
        )
        if (response.data.success) {
          settable_data(response.data.report)
        }
      } catch (error) {
        console.log(error)
      } finally {
        setLoading(false)
      }
    }

    getLogs()
  }, [])

  const today = moment().utc()
  const lastMonday = today.clone().startOf("isoWeek").subtract(1, "weeks")
  const lastSunday = lastMonday.clone().endOf("isoWeek")

  const exportToExcel = () => {
    const exportData = table_data.map(item => ({
      "Employee Name": item?.employeeName,
      "Weekday Hours": item.weekdayHours,
      "Weekday Idle Hours": item.idleWeekdayHours,
      "Weekday Extra Hours": item.extraWeekdayHours,
      "Weekend Hours": item.weekendHours,
      "Weekend Idle Hours": item.idleWeekendHours,
      "Weekend Extra Hours": item.extraWeekendHours,
    }))

    const worksheet = XLSX.utils.json_to_sheet(exportData)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      `${lastMonday.format("DD-MM-YY")} to ${lastSunday.format("DD-MM-YY")}`
    )

    const maxWidths = Object.keys(exportData[0] || {}).map(key => ({
      wch: Math.max(
        key.length,
        ...exportData.map(item => String(item[key]).length)
      ),
    }))
    worksheet["!cols"] = maxWidths

    XLSX.writeFileXLSX(
      workbook,
      `Employee_Weekly_Report_${moment().format("DD_MM_YYYY_hh_mm_ss")}.xlsx`
    )
  }

  document.title = "Weekly Report | Peacock Accountants Limited"

  return (
    <>
      {loading && <Loader />}

      <div className="page-content mb-5">
        <Container fluid>
          <Breadcrumb title={"Dashboard"} breadcrumbItem={"Weekly Report"} />

          <Card className="px-0 mx-0">
            <CardBody>
              <TableComponent
                table_data={table_data}
                toggleLogsModal={toggleLogsModal}
                setSelectedLogs={setSelectedLogs}
                exportToExcel={exportToExcel}
              />
              <Modal
                isOpen={logsModal}
                toggle={toggleLogsModal}
                size="xl"
                centered={true}
              >
                <ModalHeader toggle={toggleLogsModal}>Weekly Logs</ModalHeader>
                <ModalBody>
                  <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                    <Table className="table-bordered">
                      <thead>
                        <tr className="bg-primary text-white">
                          <th>#</th>
                          <th>Client Code</th>
                          <th>Task Name</th>
                          <th>Service</th>
                          <th>Work Type</th>
                          <th>Start Time</th>
                          <th>End Time</th>
                          <th>Worked On</th>
                          {/* <th>Work Description</th> */}
                        </tr>
                      </thead>
                      <tbody>
                        {selectedLogs.map((log, index) => (
                          <tr key={log.id}>
                            <td>{index + 1}</td>
                            <td>{log.task.client.clientCode}</td>
                            <td>{log.task.title}</td>
                            <td>{log.task.serviceName.serviceName}</td>
                            <td>{log?.workType?.workType || 'N/A'}</td>
                            <td>{log.startTime}</td>
                            <td>{log.endTime}</td>
                            <td>{moment(log.createdAt).format("DD/MM/YY")}</td>
                            {/* <td
                              dangerouslySetInnerHTML={{
                                __html: log.workDescription,
                              }}
                            /> */}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </ModalBody>
              </Modal>
            </CardBody>
          </Card>
        </Container>
      </div>
    </>
  )
}

const TableComponent = ({
  table_data,
  toggleLogsModal,
  setSelectedLogs,
  exportToExcel,
}) => {
  const { checkAccess } = usePermissionData()
  const module_id = 24
  const navigate = useNavigate()

  const columns = useMemo(
    () => [
      {
        Header: "#",
        accessor: "employeeId",
        filterable: true,
        disableFilters: true,
        Cell: cellProps => <span>{cellProps.row.index + 1}</span>,
      },
      {
        Header: "Employee",
        accessor: "employeeName",
        disableFilters: true,
        filterable: true,
        className: "col-2",
        Cell: cellProps => {
          return (
            <div className="d-flex align-items-center">
              <div className="text-center employee-margin-right">
                <img
                  src={
                    cellProps.row.original?.employeePhoto
                      ? `${process.env.REACT_APP_IMAGE_PATH}/${cellProps.row.original?.employeePhoto}`
                      : practice_logo
                  }
                  alt="img"
                  className="rounded-circle"
                  width={35}
                  height={35}
                />
              </div>
              <div>{cellProps.row.original?.employeeName}</div>
            </div>
          )
        },
      },
      {
        Header: "Weekday Hrs",
        accessor: "weekdayHours",
        disableFilters: true,
        className: " text-center",
      },
      {
        Header: "Weekday Idle Hrs",
        accessor: "idleWeekdayHours",
        disableFilters: true,
        className: " text-center",
      },
      {
        Header: "Weekday Extra Hrs",
        accessor: "extraWeekdayHours",
        disableFilters: true,
        className: "text-center",
      },
      {
        Header: "Weekend Hrs",
        accessor: "weekendHours",
        disableFilters: true,
        className: "text-center",
      },
      {
        Header: "Weekend Idle Hrs",
        accessor: "idleWeekendHours",
        disableFilters: true,
        className: "text-center",
      },
      {
        Header: "Weekend Extra Hrs",
        accessor: "extraWeekendHours",
        disableFilters: true,
        className: "text-center",
      },
      {
        Header: "Logs",
        accessor: "weeklyLogs",
        disableFilters: true,
        filterable: true,
        Cell: cellProps => (
          <div className="d-flex justify-content-center">
            <button
              title="View Logs"
              className="btn btn-info bg-info btn-sm"
              onClick={() => {
                setSelectedLogs(cellProps.row.original.weeklyLogs)
                toggleLogsModal()
              }}
            >
              <i className="fas fa-list"></i>
            </button>
          </div>
        ),
      },
    ],
    []
  )

  const today = moment().utc()
  const lastMonday = today.clone().startOf("isoWeek").subtract(1, "weeks")
  const lastSunday = lastMonday.clone().endOf("isoWeek")

  const data = useMemo(() => {
    return table_data
  }, [table_data])

  return (
    <PermittedComponent isAccess={checkAccess(module_id, "view")}>
      <CommonTable
        columns={columns}
        data={data}
        heading={
          <>
            <div>{`Weekly Report (${lastMonday.format(
              "D MMMM "
            )} - ${lastSunday.format("D MMMM")})`}</div>
          </>
        }
      >
        <div className="d-flex gap-2">
          <div>
            <button
              onClick={exportToExcel}
              className="btn btn-primary bg-primary ms-2"
            >
              <i className="fas fa-download me-3"></i>
              Export
            </button>
          </div>
          <div>
            <button
              onClick={() => navigate(-1)}
              className="btn btn-secondary bg-dark"
            >
              <i className="fas fa-arrow-left me-3"></i>
              Back
            </button>
          </div>
        </div>
      </CommonTable>
    </PermittedComponent>
  )
}

export default EmployeeWeeklyReport
